// src/components/LeaseModal.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Calendar, DollarSign, User, Warehouse, FileText, AlertCircle, RefreshCw } from 'lucide-react';
import LeaseTypeManager from './hangar/LeaseTypeManager';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const LeaseModal = ({ isOpen, onClose, onSuccess, hangar = null }) => {
  const [formData, setFormData] = useState({
    hangar_id: hangar?.id || '',
    tenant_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    monthly_rent: hangar?.monthly_rent || 0,
    ground_rent: 0,
    security_deposit: 0,
    payment_day: 1,
    is_auto_renew: false,
    aircraft_id: '',
    notes: '',
    documents: '',
    service_fees: [],
    included_services: [],
    billing_frequency: 'monthly'
  });
  
  const [leaseType, setLeaseType] = useState('active');
  const [hangars, setHangars] = useState([]);
  const [clients, setClients] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [texasLawAccepted, setTexasLawAccepted] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [hangarsRes, clientsRes, companyRes] = await Promise.all([
        axios.get(`${API_BASE}/hangars/`),
        axios.get(`${API_BASE}/owners/`),
        axios.get(`${API_BASE}/company/settings`).catch(() => ({ data: null }))
      ]);

      const availableHangars = hangarsRes.data.filter(h =>
        String(h.status).toLowerCase() === 'available'
      );

      setHangars(availableHangars);
      setClients(clientsRes.data);
      setCompany(companyRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      alert('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const getDaySuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const getTenantName = (tenant) => {
    if (!tenant) return '_______________';
    if (tenant.company_name) return tenant.company_name;
    return `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim() || tenant.email || '_______________';
  };

  const getTenantAddress = (tenant) => {
    if (!tenant) return '';
    const parts = [];
    if (tenant.address) parts.push(tenant.address);
    if (tenant.city || tenant.state || tenant.zip_code) {
      const cityStateZip = `${tenant.city || ''}${tenant.city && tenant.state ? ', ' : ''}${tenant.state || ''} ${tenant.zip_code || ''}`.trim();
      if (cityStateZip) parts.push(cityStateZip);
    }
    return parts.join('\n');
  };

  const getLessorAddress = () => {
    if (!company) return 'Boerne, Texas';
    const parts = [];
    if (company.address) parts.push(company.address);
    const cityStateZip = `${company.city || ''}${company.city && company.state ? ', ' : ''}${company.state || ''} ${company.zip_code || ''}`.trim();
    if (cityStateZip) parts.push(cityStateZip);
    return parts.join('\n') || 'Boerne, Texas';
  };

  const generateDefaultTerms = () => {
    const paymentDay = formData.payment_day;
    const suffix = getDaySuffix(paymentDay);
    const securityDepositAmount = formData.security_deposit;
    const monthlyRentAmount = leaseType === 'active' ? formData.monthly_rent : formData.ground_rent;
    const county = 'Bexar';
    
    const selectedTenant = clients.find(c => c.id === formData.tenant_id);
    const tenantName = getTenantName(selectedTenant);
    const tenantAddress = getTenantAddress(selectedTenant);
    const tenantEmail = selectedTenant?.email || '';
    const tenantPhone = selectedTenant?.phone_office || selectedTenant?.phone_mobile || selectedTenant?.phone || '';
    
    const selectedHangar = hangars.find(h => h.id === formData.hangar_id);
    const hangarNumber = selectedHangar?.hangar_number || '—';
    const hangarName = selectedHangar?.name || '';
    const hangarLocation = selectedHangar?.location || '';
    const hangarSurface = selectedHangar?.surface_sqft ? `${Number(selectedHangar.surface_sqft).toLocaleString()} sq ft` : '';
    
    const lessorName = company?.name || 'Boerne Stage Field FBO';
    const lessorAddress = getLessorAddress();
    const lessorEmail = company?.email || '';
    const lessorPhone = company?.phone || '';

    const leaseTypeText = leaseType === 'active' 
      ? 'HANGAR LEASE AGREEMENT' 
      : leaseType === 'ground' 
        ? 'GROUND LEASE AGREEMENT' 
        : 'SERVICES ONLY AGREEMENT';

    const rentDescription = leaseType === 'active'
      ? `Monthly rent of $${monthlyRentAmount.toLocaleString()} is due on the ${paymentDay}${suffix} day of each month.`
      : leaseType === 'ground'
        ? `Monthly ground rent of $${monthlyRentAmount.toLocaleString()} is due on the ${paymentDay}${suffix} day of each month. Tenant owns the hangar structure.`
        : `This is a services-only agreement. No rent for land or hangar. Only service fees apply.`;

    // Build services text
    let servicesText = '';
    if ((formData.service_fees || []).length > 0) {
      servicesText = '\n\nSERVICE FEES:\n';
      formData.service_fees.forEach(fee => {
        servicesText += `- ${fee.name}: $${fee.amount} / ${fee.frequency}\n`;
      });
    }
    if ((formData.included_services || []).length > 0) {
      servicesText += '\nSERVICES INCLUDED IN RENT:\n';
      formData.included_services.forEach(service => {
        const serviceNames = { water: 'Water', electricity: 'Electricity', trash: 'Trash Collection', wifi: 'WiFi', hvac: 'HVAC', security: 'Security' };
        servicesText += `- ${serviceNames[service] || service}\n`;
      });
    }

    return `COMMERCIAL ${leaseTypeText}
(Texas Property Code - Chapter 93 Commercial Tenancies)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

LESSOR (Landlord):
${lessorName}
${lessorAddress}
${lessorEmail ? `Email: ${lessorEmail}` : ''}
${lessorPhone ? `Phone: ${lessorPhone}` : ''}

LESSEE (Tenant):
${tenantName}
${tenantAddress ? `${tenantAddress}` : ''}
${tenantEmail ? `Email: ${tenantEmail}` : ''}
${tenantPhone ? `Phone: ${tenantPhone}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

PROPERTY INFORMATION:
${leaseType === 'active' ? 'Hangar' : leaseType === 'ground' ? 'Ground Parcel' : 'Location'}: ${hangarNumber}${hangarName ? ` - ${hangarName}` : ''}
${hangarLocation ? `Location: ${hangarLocation}` : ''}
${hangarSurface ? `Surface Area: ${hangarSurface}` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. LEASE TYPE & TERM: ${leaseType === 'active' ? 'This is a standard hangar lease.' : leaseType === 'ground' ? 'This is a ground lease. Tenant owns the hangar structure.' : 'This is a services-only agreement.'} Tenant is presumed to have abandoned the premises if goods, equipment, or other property, in an amount substantial enough to indicate a probable intent to abandon the premises, is being or has been removed from the premises and the removal is not within the normal course of the tenant's business. (Texas Property Code § 93.002(d))

2. RENT: ${rentDescription} Late fees shall be assessed as permitted by Texas Property Code § 93.012.
${servicesText}

3. SECURITY DEPOSIT: A deposit of $${securityDepositAmount.toLocaleString()} is held by Landlord. Landlord shall refund the deposit within 60 days of Tenant surrendering the premises, less lawful deductions. Failure to refund within 60 days creates presumption of bad faith. (Texas Property Code § 93.005 and § 93.011)

4. AUTO-RENEWAL: This lease ${formData.is_auto_renew ? 'WILL' : 'WILL NOT'} automatically renew at the end of the term unless terminated with 30 days written notice.

5. LANDLORD'S LIEN (PROTECTION): Landlord maintains a contractual and statutory lien under Texas Property Code Chapter 54 on all property, including aircraft and equipment stored on the premises, to secure payment of rent and performance of this lease. Lien covers rent due and rent to become due during the current 12-month period (TPC § 54.021). For rent more than 6 months past due, a lien statement must be filed with the county clerk (TPC § 54.022).

6. FAA & AERONAUTICAL USE: Use is strictly limited to aeronautical purposes per FAA Grant Assurance 22. Permitted uses: active aircraft storage, aircraft maintenance, amateur-built aircraft construction. Prohibited uses: residence, vehicle storage (non-aeronautical), household storage, non-aeronautical business. (FAA Hangar Use Policy, June 2016). Violation constitutes material breach.

7. HAZARDOUS MATERIALS & ENVIRONMENT: Tenant shall not store or discharge hazardous materials (including AVGAS, Jet-A, oils, solvents) except in aircraft tanks. Tenant is 100% liable for environmental remediation and shall indemnify and hold harmless Landlord and Airport Authorities against any EPA/TCEQ fines, penalties, or cleanup costs.

8. INSURANCE & INDEMNITY: Tenant must maintain Hangar Keeper's Liability insurance with minimum $1,000,000 per occurrence coverage. Tenant agrees to INDEMNIFY, DEFEND, and HOLD HARMLESS Landlord, Airport Management, and Airport Authorities from all claims, damages, or injuries arising from Tenant's use or occupancy of the premises.

9. TAXES (POSSESSORY INTEREST): Tenant acknowledges responsibility for all property taxes or "possessory interest" taxes related to the leased premises. Landlord shall provide Texas Tax Code § 23.1241 disclosure separately.

10. RIGHT OF ENTRY: Landlord, Airport Management, and FAA officials reserve the right to enter the premises for safety inspections, fire marshal compliance, or emergencies without prior notice. For non-emergencies, Landlord shall provide 24 hours notice.

11. MAINTENANCE: Tenant responsible for daily upkeep, cleanliness, and maintaining the premises in good condition. Landlord responsible for structural repairs to roof, walls, and foundation.

12. TERMINATION: For non-payment of rent, Landlord may pursue eviction and enforce the Landlord's Lien under Chapter 54. For abandoned premises, Landlord reserves rights available under TPC § 93.002. Early termination requires 30 days written notice plus a fee equal to 2 months' rent.

13. GOVERNING LAW & VENUE: This agreement is governed by the laws of the State of Texas, specifically Chapters 54 and 93 of the Texas Property Code. Exclusive venue for any legal action arising from this lease shall be in ${county} County, Texas.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

By signing below, both parties acknowledge receipt of this agreement, have read and understand all terms, and agree to be bound thereby.
`;
  };

  const handleSubmit = async () => {
    if (!formData.hangar_id || !formData.tenant_id || !formData.start_date) {
      alert('Please fill in all required fields');
      return;
    }
    if (!texasLawAccepted) {
      alert('Please accept the Texas Property Code terms and conditions');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        hangar_id: formData.hangar_id,
        tenant_id: formData.tenant_id,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        monthly_rent: leaseType === 'active' ? formData.monthly_rent : 0,
        ground_rent: leaseType === 'ground' ? formData.ground_rent : 0,
        lease_type: leaseType,
        service_fees: formData.service_fees,
        included_services: formData.included_services,
        billing_frequency: formData.billing_frequency,
        security_deposit: formData.security_deposit,
        payment_day: formData.payment_day,
        is_auto_renew: formData.is_auto_renew,
        aircraft_id: formData.aircraft_id || null,
        notes: formData.notes,
        documents: formData.documents || generateDefaultTerms()
      };

      const response = await axios.post(`${API_BASE}/leases/`, payload);

      await generateInitialInvoice(response.data.id);

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error:', err);
      alert(err.response?.data?.detail || 'Error creating lease');
    } finally {
      setSaving(false);
    }
  };

  const generateInitialInvoice = async (leaseId) => {
    try {
      await axios.post(`${API_BASE}/invoices/generate-from-lease`, {
        lease_id: leaseId,
        include_deposit: true,
        include_first_month: true
      });
    } catch (err) {
      console.error('Error generating invoice:', err);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-xl w-full max-w-2xl p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3" />
          <p className="text-gray-400">Loading available hangars...</p>
        </div>
      </div>
    );
  }

  const logoUrl = company?.logo_url ? `${API_BASE}${company.logo_url}` : null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex justify-between items-center sticky top-0 bg-gray-800 z-10">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Logo"
                className="h-8 w-auto object-contain rounded"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="h-8 w-8 bg-gray-600 rounded flex items-center justify-center">
                <Warehouse size={16} className="text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="text-white font-semibold">New Hangar Lease</h3>
              <p className="text-gray-400 text-xs mt-0.5">Compliant with Texas Property Code Chapter 93</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">

          {/* Hangar Selection */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">
              <Warehouse size={14} className="inline mr-1" /> Hangar *
            </label>
            {hangars.length === 0 ? (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-center">
                <p className="text-red-400 text-sm">No available hangars!</p>
                <p className="text-gray-400 text-xs mt-1">Please create a hangar first in the Hangars tab.</p>
              </div>
            ) : (
              <select
                value={formData.hangar_id}
                onChange={(e) => {
                  const selected = hangars.find(h => h.id === e.target.value);
                  setFormData({
                    ...formData,
                    hangar_id: e.target.value,
                    monthly_rent: selected?.monthly_rent || 0
                  });
                }}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              >
                <option value="">-- Select a hangar --</option>
                {hangars.map(h => (
                  <option key={h.id} value={h.id}>
                    {h.hangar_number} - {h.name || 'Unnamed'} ({h.surface_sqft} sq ft) - ${Number(h.monthly_rent).toLocaleString()}/mo
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Tenant Selection */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">
              <User size={14} className="inline mr-1" /> Tenant *
            </label>
            <select
              value={formData.tenant_id}
              onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
            >
              <option value="">-- Select a tenant --</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>
                  {c.company_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email}
                </option>
              ))}
            </select>
          </div>

          {/* Lease Type Manager */}
          <LeaseTypeManager
            leaseType={leaseType}
            setLeaseType={setLeaseType}
            formData={formData}
            setFormData={setFormData}
          />

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm block mb-1">
                <Calendar size={14} className="inline mr-1" /> Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">
                <Calendar size={14} className="inline mr-1" /> End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              />
            </div>
          </div>

          {/* Amounts */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-sm block mb-1">
                <DollarSign size={14} className="inline mr-1" /> Security Deposit
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.security_deposit}
                onChange={(e) => setFormData({...formData, security_deposit: parseFloat(e.target.value) || 0})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              />
            </div>
            <div>
              <label className="text-gray-400 text-sm block mb-1">Payment Day of Month</label>
              <input
                type="number"
                min="1"
                max="28"
                value={formData.payment_day}
                onChange={(e) => setFormData({...formData, payment_day: parseInt(e.target.value) || 1})}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white"
              />
            </div>
          </div>

          {/* Auto-renew */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="autoRenew"
              checked={formData.is_auto_renew}
              onChange={(e) => setFormData({...formData, is_auto_renew: e.target.checked})}
              className="w-4 h-4 accent-blue-500"
            />
            <label htmlFor="autoRenew" className="text-gray-300 text-sm flex items-center gap-1">
              <RefreshCw size={13} /> Auto-renew lease
            </label>
          </div>

          {/* Notes */}
          <div>
            <label className="text-gray-400 text-sm block mb-1">Special Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows={2}
              placeholder="Special conditions, etc."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            />
          </div>

          {/* Texas Legal Box */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle size={16} className="text-yellow-400 mt-0.5 shrink-0" />
              <div className="text-xs text-gray-300">
                <p className="font-semibold text-yellow-400 mb-1">Texas Property Code Chapter 93 (Commercial Tenancies):</p>
                <p>✓ Security deposit must be returned within 60 days (TPC § 93.005)</p>
                <p>✓ Late fees are regulated (TPC § 93.012)</p>
                <p>✓ Landlord's Lien on aircraft and property (TPC Chapter 54)</p>
                <p>✓ Abandonment defined by removal outside normal business (TPC § 93.002(d))</p>
                <p>✓ Property tax disclosure required (Texas Tax Code § 23.1241)</p>
                <p>✓ FAA Grant Assurance 22 - Aeronautical use only</p>
              </div>
            </div>
          </div>

          {/* Texas Law Acceptance */}
          <div className="flex items-center gap-3 border-t border-gray-700 pt-3">
            <input
              type="checkbox"
              id="texasLaw"
              checked={texasLawAccepted}
              onChange={(e) => setTexasLawAccepted(e.target.checked)}
              className="w-4 h-4 accent-green-500"
            />
            <label htmlFor="texasLaw" className="text-gray-300 text-sm">
              I confirm this lease complies with Texas Property Code Chapter 93 (Commercial Tenancies), Texas Tax Code, and FAA airport regulations
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3 sticky bottom-0 bg-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !formData.hangar_id || !formData.tenant_id || !texasLawAccepted}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white flex items-center gap-2"
          >
            <FileText size={16} />
            {saving ? 'Creating...' : 'Create Lease & Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeaseModal;